import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentUserProfile } from "@/hooks/useCurrentUserProfile";

/**
 * Email verification status + resend helper.
 *
 * Verification is tracked in `profiles.email_verified` via our own
 * verification-email flow. It NEVER blocks access to the app — it is purely
 * informational so users can confirm their email when convenient.
 */
export function useEmailVerification() {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile();
  const [isSending, setIsSending] = useState(false);

  const isVerified = !!(profile as any)?.email_verified;
  // Only treat as "unverified" once we actually have a profile loaded.
  const isUnverified = !!user && !!profile && !isVerified;

  const resendVerification = async (): Promise<{ error: string | null }> => {
    if (!user?.id || !user?.email) {
      return { error: "You need to be signed in to resend the verification email." };
    }
    setIsSending(true);
    try {
      const name =
        (profile as any)?.name ||
        (user.user_metadata as any)?.name ||
        user.email.split("@")[0];
      const { error } = await supabase.functions.invoke("send-verification-email", {
        body: { userId: user.id, email: user.email, name },
      });
      if (error) throw error;
      return { error: null };
    } catch (e: any) {
      return { error: e?.message || "Could not send the verification email. Please try again." };
    } finally {
      setIsSending(false);
    }
  };

  return { isVerified, isUnverified, isSending, resendVerification, email: user?.email };
}
